import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { EntityDialogForm } from "components/FormDialog/EntityDialogForm";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import React from "react";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaGavel } from "react-icons/fa";
import { addRule, adminGetAllRules, deleteRule, updateRule } from "redux/features/Admin/rules/rulesSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";

export interface RulesPageProps {
  className?: string;
}

interface Rule {
  id: number;
  designation: string;
}

interface ModalState {
  isRule: boolean;
  isDeleteModalOpen: boolean;
  selectedItem: Rule | null;
  loading: boolean;
}

const RulesPage: FC<RulesPageProps> = React.memo(({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    rules: rulesList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    rules: state.featureRules?.allRules?.rules || [],
    totalCount: state.featureRules?.allRules?.totalCount || 0,
    totalPages: state.featureRules?.allRules?.totalPages || 0,
    currentPage: state.featureRules?.allRules?.currentPage || 1,
    loading: state.featureRules?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isRule: false,
    isDeleteModalOpen: false,
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllRules({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleEdit = (item: Rule) => {
    setModalState({ ...modalState, selectedItem: item, isRule: true });
  };

  const handleDeleteModalOpen = (rule: Rule) => {
    setModalState({ ...modalState, selectedItem: rule, isDeleteModalOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteRule(modalState.selectedItem.id));
      setModalState((prev) => ({ ...modalState, isDeleteModalOpen: false, loading: false, selectedItem: null }));
      await dispatch(adminGetAllRules({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleConfirmUpdate = async (payload: { designation: string }) => {
    if (!modalState.selectedItem) return;
    setModalState({ ...modalState, loading: true });
    await dispatch(updateRule({ data: payload, id: modalState.selectedItem.id })).then(() => {
      setModalState({ ...modalState, loading: false, isRule: false, selectedItem: null });
      dispatch(adminGetAllRules({ page, searchTerm: debouncedSearchTerm }));
    });
  };

  const handleTableAction = (action: string, item: Rule) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "edit") {
      handleEdit(item);
    }
  };

  const columns = [
    {
      key: "designation",
      label: "Désignation",
      render: (item: Rule) => <span>{item.designation}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaGavel className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Liste des règles</h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary
                onClick={() => setModalState({ ...modalState, isRule: true })}
                className="btn-primary-gradient"
                sizeClass="px-4 py-2"
              >
                Ajouter une règle
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par désignation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search rules"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={() => {
                    setModalState({ ...modalState, isDeleteModalOpen: false, selectedItem: null });
                  }}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.designation || "",
                  }}
                />
              )}

              {modalState.isRule && (
                <EntityDialogForm
                  open={modalState.isRule}
                  handleClose={() =>
                    setModalState({
                      ...modalState,
                      isRule: false,
                      selectedItem: null,
                      isDeleteModalOpen: false,
                    })
                  }
                  isLoading={modalState.loading}
                  addfn={(payload: any) => dispatch(addRule(payload))}
                  updatefn={handleConfirmUpdate}
                  id={modalState.selectedItem?.id}
                  value={modalState.selectedItem?.designation}
                  addfnTitle="Ajouter une règle"
                  updatefnTitle="Modifier la règle"
                  onSubmitSuccess={() => {
                    dispatch(adminGetAllRules({ page: 1, searchTerm: "" }));
                  }}
                />
              )}

              <DynamicTable
                columns={columns}
                data={rulesList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["edit", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
});

export default RulesPage;
